import { AxiosResponse } from 'axios';
import { onlyNumbers } from 'shared/helpers/string';
import { IAppResponse } from 'shared/models/app';
import { IAppUserResponse } from 'shared/models/appUser';
import { IInstallationStatus } from 'shared/models/installationStatus';
import { IManifesto } from 'shared/models/manifesto';
import { ITelemetriaRobo } from 'shared/models/telemetriaRobo';
import { apiInstallationApp, apiNestNode, apiV2, nodeApi } from './http';
import {
  AppInfoRequestData,
  AuthPayload,
  AuthResponse,
  QuickConfigsRequestData,
} from './interfaces/app';

export function searchApps(
  query: string,
): Promise<AxiosResponse<IAppResponse>> {
  return apiV2.get<IAppResponse>(`installation/app/${query}`);
}

export function searchAppsWithYoogaCustomerId(
  document: string,
  yoogaCustomerId: string,
): Promise<AxiosResponse<IAppResponse>> {
  return apiV2.get<IAppResponse>(
    `installation/app/${document}/yooga-customer-id/${yoogaCustomerId}`,
  );
}

export function getAppUsers(
  idi: number,
): Promise<AxiosResponse<IAppUserResponse>> {
  return apiV2.get<IAppUserResponse>(`installation/app/${idi}/users`);
}

export function updateUser(
  idi: number,
  payload: {
    codigo: number;
    login: string;
    nivel_acesso: 0 | 25 | 50 | 75 | 100 | 125 | 175;
    password?: string;
  },
): Promise<AxiosResponse> {
  return apiV2.put(`installation/app/${idi}/user/${payload.codigo}`, {
    login: payload.login,
    accessLevel: payload.nivel_acesso,
    password: payload.password,
  });
}

export function createTempUser(
  idi: number,
  login: string,
  password: string,
): Promise<AxiosResponse<{ status: boolean; message: string }>> {
  return apiInstallationApp.post<{ status: boolean; message: string }>(
    `${idi}/create-temporary-user`,
    {
      login,
      password,
    },
  );
}

export function updateAppInfo(
  idi: number,
  payload: AppInfoRequestData,
): Promise<AxiosResponse> {
  return apiNestNode.put(`installation/${idi}`, payload);
}

export function trustUnlock(
  idi: number,
  customerId: number,
  resetConsecutiveBillingUnlocks: boolean,
): Promise<AxiosResponse<{ message: string }>> {
  return apiNestNode.post(`trust-unlock-installation/${idi}`, {
    customerId,
    resetConsecutiveBillingUnlocks,
  });
}

export function temporaryUnlock(
  idi: number,
  customerId: number,
): Promise<AxiosResponse<{ message: string }>> {
  return apiNestNode.post(`temporary-unlock-installation/${idi}`, {
    customerId,
  });
}

export function appReset(
  payload: QuickConfigsRequestData,
): Promise<AxiosResponse> {
  return nodeApi.post('instalacoes/resetdata', {
    ...payload,
    copy_secret: 'p8PfF2QWzp9u9MYiuxLKYcTS8fIzZ0Wn',
  });
}

export function blockApp(
  idi: number,
  customerId: number,
): Promise<AxiosResponse> {
  return apiNestNode.post(`block-installation/${idi}`, { customerId });
}

export function blockAllApps(
  document: string,
  customerId: number,
): Promise<AxiosResponse> {
  return apiNestNode.post(`block-all-installations/${onlyNumbers(document)}`, {
    customerId,
  });
}

export function removeTrialBannerAllApps(
  document: string,
  customerId: number,
): Promise<AxiosResponse> {
  return apiV2.post(
    `installation/app/remove-trial-banner/${onlyNumbers(document)}`,
    {
      customerId,
    },
  );
}

export function getInstallationStatusHistory(
  idi: number,
): Promise<AxiosResponse<IInstallationStatus[]>> {
  return apiV2.get(`installation/${idi}`);
}

export function getRoboFiscal(cnpj: string): Promise<AxiosResponse> {
  return apiV2.get(`installation/fiscal/robo/${cnpj}`);
}

export function getManifesto(
  nProt: string,
): Promise<AxiosResponse<IManifesto>> {
  return apiV2.get(`installation/fiscal/manifesto/${nProt}`);
}

export function getTelemetriaRobo(
  clienteId: number,
): Promise<AxiosResponse<ITelemetriaRobo[]>> {
  return apiV2.get(`installation/fiscal/telemetria/${clienteId}`);
}

export async function authenticate(
  document: string,
  password: string,
): Promise<AuthResponse> {
  const payload: AuthPayload = {
    login_api: document,
    senha_api: password,
  };
  try {
    const response = await nodeApi.post<AuthResponse>(
      'https://api4.yooga.com.br/authenticate',
      payload,
    );
    return response.data;
  } catch (error) {
    console.error('Erro na autenticação:', error);
    throw error;
  }
}
